import React, { useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import { isEqual } from 'lodash';

import BriefModule from 'features/briefs/components/atoms/BriefModule';
import BriefProductLinks from 'features/briefs/components/atoms/BriefProductLinks';
import CheckboxDisplay from 'features/briefs/components/atoms/CheckboxDisplay';
import { getBriefFormConfig } from 'features/briefs/components/organisms/BriefMultiPageForm/config';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import { useRightDrawer } from 'features/ui/useRightDrawer';
import NotificationBanner from 'shared/components/atoms/NotificationBanner';
import Tab from 'shared/components/atoms/Tab';
import { Box, Button, Flex, Text } from 'shared/components/display';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import MultiPageLayout from 'shared/components/molecules/MultiPageLayout';
import Richtext, { RICHTEXT_DEFAULT_VALUE } from 'shared/components/Richtext';

import { BRIEF_COPY } from 'shared/config/copy';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { intl } from 'shared/lib/intl';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import COLORS from 'shared/styles/colors';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { Brief } from 'shared/typings/briefs';
import { formatCurrency } from 'shared/utilities/stringUtility';

import styles from './styles';

interface Props {
  brief: Brief;
  loading?: boolean;
  className?: string;
}

const BrandFullBriefView: React.FC<Props> = ({ brief, loading = false, className = '' }) => {
  const [modTab, setModTab] = useState(0);
  const { isSuperadmin = false } = useCurrentUser();
  const { STEP_TITLES } = getBriefFormConfig(false);
  const { setRightDrawer } = useRightDrawer(true);

  const toggleRightDrawerInfo = ({ key, text, header }: { key: string; text: string; header: string }) => {
    setRightDrawer({
      key,
      Content: (
        <Text variant={TEXT_VARIANTS.BODY} color={COLORS.NEUTRAL700}>
          {ReactHtmlParser(text)}
        </Text>
      ),
      headerText: header,
    });
  };

  return (
    <Box css={styles} className={cs('brand-full-brief-view', className)}>
      {loading ? (
        <CenteredSpinner />
      ) : (
        <MultiPageLayout ButtonComponent={Button}>
          <MultiPageLayout.Page title={STEP_TITLES.Overview.title} titleIcon={STEP_TITLES.Overview.icon} isFirstPage>
            <DefinitionList labelColor={COLORS.NEUTRAL800} key={STEP_TITLES.Overview.title}>
              <ListDetail
                text="Brief Name"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
              >
                {brief.name}
              </ListDetail>
              <ListDetail
                text="Brief Overview"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
                hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.description))}
              >
                <Richtext value={JSON.parse(brief.description)} key={`${brief.id}-description`} readOnly />
              </ListDetail>
              <ListDetail
                text="Product / Service"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
                hide={!brief?.website?.length && !brief.shippingRequired}
              >
                {brief?.website?.length ? (
                  <BriefProductLinks website={brief.website} className="brand-full-brief-view__product-links" />
                ) : (
                  <></>
                )}
                {brief.shippingRequired ? (
                  <CheckboxDisplay
                    className="brand-full-brief-view__shipping-required"
                    text="Shipping Required"
                    disabled={!brief.shippingRequired}
                  />
                ) : (
                  <></>
                )}
              </ListDetail>
            </DefinitionList>
          </MultiPageLayout.Page>
          {isSuperadmin && (
            <MultiPageLayout.Page title={STEP_TITLES.Pricing.title} titleIcon={STEP_TITLES.Pricing.icon}>
              <DefinitionList labelColor={COLORS.NEUTRAL800}>
                <NotificationBanner theme="info-alt" message={BRIEF_COPY.BANNER_INTERNAL_VISIBILITY} />
                <ListDetail
                  dataCy="brand-full-brief-view__payout"
                  text={BRIEF_COPY.LABEL_CREATOR_PAYOUT}
                  labelClassName="brand-full-brief-view__heading"
                  detailsClassName="brand-full-brief-view__detail"
                >
                  {intl.formatMessage(
                    {
                      id: 'DETAIL_PERFORMANCE_PAYOUT',
                    },
                    {
                      payout: formatCurrency(brief.type === 0 ? 0 : brief.creatorPayout),
                      creativeOnly: brief.creativeOnly,
                    },
                  )}
                </ListDetail>
                <ListDetail
                  text={BRIEF_COPY.LABEL_ELITE_CREATOR_PAYOUT}
                  labelClassName="brand-full-brief-view__heading"
                  detailsClassName="brand-full-brief-view__detail"
                >
                  {intl.formatMessage(
                    {
                      id: 'DETAIL_PERFORMANCE_PAYOUT',
                    },
                    {
                      payout: formatCurrency(brief.type === 0 ? 0 : brief.eliteCreatorPayout),
                      creativeOnly: brief.creativeOnly,
                    },
                  )}
                </ListDetail>
                <ListDetail
                  text={BRIEF_COPY.LABEL_CREATOR_APPROVAL_PRICING}
                  hide={!isSuperadmin}
                  labelClassName="brand-full-brief-view__heading"
                  detailsClassName="brand-full-brief-view__detail"
                >
                  {formatCurrency(brief.type === 0 ? 0 : brief.creatorApprovalPricing)}
                </ListDetail>
                <ListDetail
                  text={BRIEF_COPY.LABEL_ELITE_CREATOR_APPROVAL_PRICING}
                  hide={!isSuperadmin}
                  labelClassName="brand-full-brief-view__heading"
                  detailsClassName="brand-full-brief-view__detail"
                >
                  {formatCurrency(brief.type === 0 ? 0 : brief.eliteCreatorApprovalPricing)}
                </ListDetail>
                <ListDetail
                  text="Internal Note"
                  hide={!brief.internalNote}
                  dataCy="brand-full-brief-view__internal-note"
                  labelClassName="brand-full-brief-view__heading"
                  detailsClassName="brand-full-brief-view__detail"
                >
                  {brief.internalNote || ''}
                </ListDetail>
              </DefinitionList>
            </MultiPageLayout.Page>
          )}
          <MultiPageLayout.Page
            title={STEP_TITLES['Creator Requirements'].title}
            titleIcon={STEP_TITLES['Creator Requirements'].icon}
          >
            <DefinitionList labelColor={COLORS.NEUTRAL800} key={STEP_TITLES['Creator Requirements'].title}>
              <ListDetail
                text="Creator Requirements"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
                hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(brief.creatorRequirements))}
              >
                <Richtext
                  value={JSON.parse(brief.creatorRequirements)}
                  key={`${brief.id}-creator-requirements`}
                  readOnly
                />
              </ListDetail>
              {brief.requireConfirmation && (
                <Flex className="brand-full-brief-view__toggle-with-info">
                  <CheckboxDisplay text="Require Creator Confirmation" disabled={!brief.requireConfirmation} />
                  <Button
                    variant={BUTTON_VARIANTS.TEXT_ONLY}
                    className="brand-full-brief-view__info-icon"
                    onClick={() =>
                      toggleRightDrawerInfo({
                        key: BRIEF_COPY.FORM_HEADING_REQUIREMENTS,
                        header: BRIEF_COPY.FORM_HEADING_REQUIREMENTS,
                        text: BRIEF_COPY.FORM_DESCRIPTION_REQUIREMENTS,
                      })
                    }
                  >
                    <FontAwesomeIcon color={COLORS.NEUTRAL400} icon={faInfoCircle} size="sm" />
                  </Button>
                </Flex>
              )}
              <ListDetail
                text="In-Shop Visit Required"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
                hide={!Boolean(brief.states?.length)}
              >
                {brief.states?.map((state) => state.code).join(', ') || 'N/A'}
              </ListDetail>
              <ListDetail
                text="Auto-Response to Approved Creators"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
                hide={!brief.autoResponse}
              >
                {brief.autoResponse}
              </ListDetail>
              {brief.inviteOnly && (
                <Flex className="brand-full-brief-view__toggle-with-info">
                  <CheckboxDisplay text="Invite to Apply Only" disabled={!brief.inviteOnly} />
                  <Button
                    variant={BUTTON_VARIANTS.TEXT_ONLY}
                    className="brand-full-brief-view__info-icon"
                    onClick={() =>
                      toggleRightDrawerInfo({
                        key: BRIEF_COPY.FORM_HEADING_INVITE_ONLY,
                        header: BRIEF_COPY.FORM_HEADING_INVITE_ONLY,
                        text: BRIEF_COPY.FORM_TOOLTIP_INVITE_ONLY,
                      })
                    }
                  >
                    <FontAwesomeIcon color={COLORS.NEUTRAL400} icon={faInfoCircle} size="sm" />
                  </Button>
                </Flex>
              )}
            </DefinitionList>
          </MultiPageLayout.Page>
          <MultiPageLayout.Page
            title={STEP_TITLES['Content Guidelines'].title}
            titleIcon={STEP_TITLES['Content Guidelines'].icon}
          >
            <DefinitionList labelColor={COLORS.NEUTRAL800} key={STEP_TITLES['Content Guidelines'].title}>
              <ListDetail
                text="Content Guidelines"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
              >
                <Richtext value={JSON.parse(brief.contentGuidelines)} key={`${brief.id}-content-guidelines`} readOnly />
              </ListDetail>
              <ListDetail
                text="Dos & Don’ts"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
              >
                <Richtext value={JSON.parse(brief.dosAndDonts)} key={`${brief.id}-dos-donts`} readOnly />
              </ListDetail>
              <ListDetail
                text="Value Propositions"
                labelClassName="brand-full-brief-view__heading"
                className="brand-full-brief-view__detail"
              >
                <Richtext value={JSON.parse(brief.valuePropositions)} key={`${brief.id}-value-propositions`} readOnly />
              </ListDetail>
            </DefinitionList>
          </MultiPageLayout.Page>
          <MultiPageLayout.Page
            title={STEP_TITLES['Module Requirements'].title}
            titleIcon={STEP_TITLES['Module Requirements'].icon}
            isLastPage
          >
            <>
              <Flex className="brand-full-brief-view__module-tabs">
                {brief.modules?.map((module, idx) => (
                  <Tab
                    key={module.id}
                    name={module.name}
                    isActive={modTab == idx}
                    onClick={() => setModTab(idx)}
                    className="brand-full-brief-view__module-tab"
                  />
                ))}
              </Flex>
              <BriefModule module={brief.modules[modTab]} />
            </>
          </MultiPageLayout.Page>
        </MultiPageLayout>
      )}
    </Box>
  );
};

export default BrandFullBriefView;
